import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { motion } from "framer-motion";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RotateCw, Crop, Trash2, GripVertical } from "lucide-react";

export const SortableImage = ({ image, onRotate, onCrop, onDelete, onCaptionChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      className="image-card bg-white rounded-lg overflow-hidden border border-border"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      data-testid={`sortable-image-${image.id}`}
    >
      <div className="relative aspect-square">
        <img
          src={image.data}
          alt={image.caption || "Hochgeladenes Bild"}
          className="w-full h-full object-cover"
          style={{ transform: `rotate(${image.rotation}deg)` }}
        />
        <div
          {...attributes}
          {...listeners}
          className="absolute top-1 left-1 p-1 bg-white/80 rounded cursor-grab hover:bg-white"
          data-testid={`drag-handle-${image.id}`}
        >
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="image-actions">
          <button
            onClick={() => onRotate(image.id)}
            className="p-1.5 bg-white/90 rounded hover:bg-white"
            title="Rotieren"
            data-testid={`rotate-btn-${image.id}`}
          >
            <RotateCw className="w-3.5 h-3.5 text-foreground" />
          </button>
          <button
            onClick={() => onCrop(image)}
            className="p-1.5 bg-white/90 rounded hover:bg-white"
            title="Zuschneiden"
            data-testid={`crop-btn-${image.id}`}
          >
            <Crop className="w-3.5 h-3.5 text-foreground" />
          </button>
          <button
            onClick={() => onDelete(image.id)}
            className="p-1.5 bg-white/90 rounded hover:bg-destructive/90 hover:text-white"
            title="Löschen"
            data-testid={`delete-btn-${image.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="p-2 bg-slate-50">
        <Label className="text-xs text-muted-foreground mb-1 block">Bildtext:</Label>
        <Textarea
          value={image.caption}
          onChange={(e) => onCaptionChange(image.id, e.target.value)}
          placeholder="Beschreibung zum Bild eingeben..."
          className="text-xs min-h-[60px] resize-none"
          data-testid={`caption-input-${image.id}`}
        />
      </div>
    </motion.div>
  );
};

export default SortableImage;
